import { useState, useEffect } from "react";

export interface MetricFilter {
  field: string;
  label: string;
  operator: ">" | "<" | ">=" | "<=" | "=";
  value: number | null;
  unit?: string;
  step?: number;
  placeholder?: string;
}

export interface MetricsFilters {
  cagr: MetricFilter;
  sharpe: MetricFilter;
  sortino: MetricFilter;
  maxDrawdown: MetricFilter;
  volatility: MetricFilter;
  calmar: MetricFilter;
  totalReturn: MetricFilter;
  bestYear: MetricFilter;
  worstYear: MetricFilter;
}

const defaultFilters: MetricsFilters = {
  cagr: {
    field: "cagr",
    label: "CAGR",
    operator: ">",
    value: null,
    unit: "%",
    step: 1,
    placeholder: "e.g. 20",
  },
  sharpe: {
    field: "sharpe",
    label: "Sharpe Ratio",
    operator: ">",
    value: null,
    step: 0.1,
    placeholder: "e.g. 1.5",
  },
  sortino: {
    field: "sortino",
    label: "Sortino Ratio",
    operator: ">",
    value: null,
    step: 0.1,
    placeholder: "e.g. 2.0",
  },
  maxDrawdown: {
    field: "max_drawdown",
    label: "Max Drawdown",
    operator: "<",
    value: null,
    unit: "%",
    step: 1,
    placeholder: "e.g. -20",
  },
  volatility: {
    field: "volatility",
    label: "Volatility",
    operator: "<",
    value: null,
    unit: "%",
    step: 1,
    placeholder: "e.g. 15",
  },
  calmar: {
    field: "calmar",
    label: "Calmar Ratio",
    operator: ">",
    value: null,
    step: 0.1,
    placeholder: "e.g. 1.0",
  },
  totalReturn: {
    field: "total_return",
    label: "Total Return",
    operator: ">",
    value: null,
    unit: "%",
    step: 10,
    placeholder: "e.g. 100",
  },
  bestYear: {
    field: "best_year",
    label: "Best Year",
    operator: ">",
    value: null,
    unit: "%",
    step: 5,
    placeholder: "e.g. 30",
  },
  worstYear: {
    field: "worst_year",
    label: "Worst Year",
    operator: ">",
    value: null,
    unit: "%",
    step: 5,
    placeholder: "e.g. -10",
  },
};

interface MetricsFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: MetricsFilters;
  onFiltersChange: (filters: MetricsFilters) => void;
  activeFilterCount: number;
}

const operators = [
  { value: ">", label: ">" },
  { value: "<", label: "<" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
];

export function MetricsFilterPanel({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  activeFilterCount,
}: MetricsFilterPanelProps) {
  const [localFilters, setLocalFilters] = useState<MetricsFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleOperatorChange = (
    key: keyof MetricsFilters,
    operator: ">" | "<" | ">=" | "<="
  ) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], operator },
    }));
  };

  const handleValueChange = (key: keyof MetricsFilters, value: string) => {
    const numValue = value === "" ? null : parseFloat(value);
    setLocalFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], value: numValue },
    }));
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const filterGroups = [
    {
      title: "Performance",
      icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
      filters: [
        { key: "cagr" as const, ...localFilters.cagr },
        { key: "totalReturn" as const, ...localFilters.totalReturn },
        { key: "bestYear" as const, ...localFilters.bestYear },
        { key: "worstYear" as const, ...localFilters.worstYear },
      ],
    },
    {
      title: "Risk-Adjusted",
      icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      filters: [
        { key: "sharpe" as const, ...localFilters.sharpe },
        { key: "sortino" as const, ...localFilters.sortino },
        { key: "calmar" as const, ...localFilters.calmar },
      ],
    },
    {
      title: "Risk",
      icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
      filters: [
        { key: "maxDrawdown" as const, ...localFilters.maxDrawdown },
        { key: "volatility" as const, ...localFilters.volatility },
      ],
    },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close filters"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-surface-900 border-l border-surface-700 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-700/50 bg-surface-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500/20 to-accent-500/20 border border-brand-500/30 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-brand-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-surface-100">
                Advanced Filters
              </h2>
              <p className="text-xs text-surface-400">
                {activeFilterCount > 0
                  ? `${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} active`
                  : "No filters applied"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-700/50 text-surface-400 hover:text-surface-200 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Filter Groups */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {filterGroups.map((group) => (
            <div key={group.title} className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-surface-300">
                <svg
                  className="w-4 h-4 text-surface-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={group.icon}
                  />
                </svg>
                {group.title}
              </div>

              <div className="space-y-2">
                {group.filters.map((filter) => (
                  <div
                    key={filter.key}
                    className="flex items-center gap-2 p-3 rounded-xl bg-surface-800/50 border border-surface-700/30 hover:border-surface-600/50 transition-colors"
                  >
                    <span className="text-sm text-surface-300 w-28 flex-shrink-0">
                      {filter.label}
                    </span>

                    {/* Operator dropdown */}
                    <select
                      value={filter.operator}
                      onChange={(e) =>
                        handleOperatorChange(
                          filter.key,
                          e.target.value as ">" | "<" | ">=" | "<="
                        )
                      }
                      className="w-14 px-2 py-1.5 rounded-lg bg-surface-700 border border-surface-600 text-surface-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500"
                    >
                      {operators.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>

                    {/* Value input */}
                    <div className="relative flex-1">
                      <input
                        type="number"
                        step={filter.step}
                        value={filter.value ?? ""}
                        onChange={(e) =>
                          handleValueChange(filter.key, e.target.value)
                        }
                        placeholder={filter.placeholder}
                        className="w-full px-3 py-1.5 rounded-lg bg-surface-700 border border-surface-600 text-surface-200 text-sm placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500"
                      />
                      {filter.unit && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 text-sm">
                          {filter.unit}
                        </span>
                      )}
                    </div>

                    {/* Clear button */}
                    {filter.value !== null && (
                      <button
                        onClick={() => handleValueChange(filter.key, "")}
                        className="p-1 rounded hover:bg-surface-600 text-surface-500 hover:text-surface-300 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Quick Presets */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-surface-300">
              <svg
                className="w-4 h-4 text-surface-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Quick Presets
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setLocalFilters({
                    ...defaultFilters,
                    cagr: { ...defaultFilters.cagr, operator: ">", value: 20 },
                    sharpe: { ...defaultFilters.sharpe, operator: ">", value: 1 },
                    maxDrawdown: {
                      ...defaultFilters.maxDrawdown,
                      operator: ">",
                      value: -25,
                    },
                  });
                }}
                className="px-3 py-2 rounded-lg bg-surface-800/50 border border-surface-700/30 hover:border-brand-500/50 hover:bg-brand-500/10 text-sm text-surface-300 hover:text-brand-400 transition-all"
              >
                🎯 Balanced
              </button>
              <button
                onClick={() => {
                  setLocalFilters({
                    ...defaultFilters,
                    cagr: { ...defaultFilters.cagr, operator: ">", value: 30 },
                    sharpe: { ...defaultFilters.sharpe, operator: ">", value: 1.5 },
                  });
                }}
                className="px-3 py-2 rounded-lg bg-surface-800/50 border border-surface-700/30 hover:border-green-500/50 hover:bg-green-500/10 text-sm text-surface-300 hover:text-green-400 transition-all"
              >
                🚀 High Growth
              </button>
              <button
                onClick={() => {
                  setLocalFilters({
                    ...defaultFilters,
                    maxDrawdown: {
                      ...defaultFilters.maxDrawdown,
                      operator: ">",
                      value: -15,
                    },
                    volatility: {
                      ...defaultFilters.volatility,
                      operator: "<",
                      value: 15,
                    },
                  });
                }}
                className="px-3 py-2 rounded-lg bg-surface-800/50 border border-surface-700/30 hover:border-blue-500/50 hover:bg-blue-500/10 text-sm text-surface-300 hover:text-blue-400 transition-all"
              >
                🛡️ Low Risk
              </button>
              <button
                onClick={() => {
                  setLocalFilters({
                    ...defaultFilters,
                    sharpe: { ...defaultFilters.sharpe, operator: ">", value: 2 },
                    sortino: { ...defaultFilters.sortino, operator: ">", value: 2.5 },
                    calmar: { ...defaultFilters.calmar, operator: ">", value: 2 },
                  });
                }}
                className="px-3 py-2 rounded-lg bg-surface-800/50 border border-surface-700/30 hover:border-purple-500/50 hover:bg-purple-500/10 text-sm text-surface-300 hover:text-purple-400 transition-all"
              >
                ⚖️ Risk-Adjusted
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-700/50 bg-surface-800/50 flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2.5 rounded-lg border border-surface-600 text-surface-300 hover:bg-surface-700/50 transition-colors text-sm font-medium"
          >
            Reset All
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-colors text-sm font-medium shadow-lg shadow-brand-500/25"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

export function getDefaultFilters(): MetricsFilters {
  return defaultFilters;
}

export function countActiveFilters(filters: MetricsFilters): number {
  return Object.values(filters).filter((f) => f.value !== null).length;
}

export function applyMetricsFilters<
  T extends {
    cagr?: number;
    sharpe?: number;
    sortino?: number;
    max_drawdown?: number;
    volatility?: number;
    calmar?: number;
    total_return?: number;
    best_year?: number;
    worst_year?: number;
  }
>(items: T[], filters: MetricsFilters): T[] {
  return items.filter((item) => {
    for (const filter of Object.values(filters)) {
      if (filter.value === null) continue;

      const fieldValue = item[filter.field as keyof T] as number | undefined;
      if (fieldValue === undefined || fieldValue === null) continue;

      const filterValue = filter.value;

      switch (filter.operator) {
        case ">":
          if (!(fieldValue > filterValue)) return false;
          break;
        case "<":
          if (!(fieldValue < filterValue)) return false;
          break;
        case ">=":
          if (!(fieldValue >= filterValue)) return false;
          break;
        case "<=":
          if (!(fieldValue <= filterValue)) return false;
          break;
      }
    }
    return true;
  });
}
